import React from 'react';
import { Alert, Avatar, Button, Space, Table, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EVENT_TYPES, EVENT_TYPE_LABELS, EventType, TeamMemberInsight } from './types';
import { DrillDownMetric } from './DrillDownDrawer';

interface Props {
    rows: TeamMemberInsight[];
    coderTemplates: string[];
    loading: boolean;
    error: string | null;
    onCellClick: (member: TeamMemberInsight, metric: DrillDownMetric, eventType?: EventType) => void;
}

const ColumnTitle: React.FC<{ label: string; hint: string }> = ({ label, hint }) => (
    <Space size={4}>
        {label}
        <Tooltip title={hint}>
            <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
        </Tooltip>
    </Space>
);

/** Seconds → compact "2h 15m" / "45m" / "3h". Zero or missing renders as an em dash. */
const formatDuration = (seconds?: number): string => {
    if (!seconds || seconds <= 0) return '–';
    const totalMin = Math.round(seconds / 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const buildColumns = (
    coderTemplates: string[],
    onCellClick: (member: TeamMemberInsight, metric: DrillDownMetric, eventType?: EventType) => void,
): ColumnsType<TeamMemberInsight> => {
    const numericClickable = (metric: DrillDownMetric, eventType?: EventType) =>
        (n: number, row: TeamMemberInsight) => {
            if (n <= 0) return <span>{n.toLocaleString()}</span>;
            return (
                <Button type="link" style={{ padding: 0 }} onClick={() => onCellClick(row, metric, eventType)}>
                    {n.toLocaleString()}
                </Button>
            );
        };

    const countEventsOfType = (row: TeamMemberInsight, type: EventType): number =>
        row.eventsItems.reduce((n, ev) => (ev.type === type ? n + 1 : n), 0);

    return [
        {
            title: <ColumnTitle label="User" hint="Member of the Sonat-com GitHub org. External contributors and bots are excluded." />,
            dataIndex: 'login',
            key: 'login',
            render: (login: string, row) => (
                <Space>
                    <Avatar src={row.avatarUrl} size="small">
                        {login.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <a
                        href={`https://github.com/${login}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {login}
                    </a>
                </Space>
            ),
            sorter: (a, b) => a.login.localeCompare(b.login),
        },
        {
            title: <ColumnTitle label="PRs merged" hint="Pull requests authored by this user that merged inside the selected window, across every non-archived repo in the org. Click to see them." />,
            dataIndex: 'prsMerged',
            key: 'prsMerged',
            align: 'right',
            sorter: (a, b) => a.prsMerged - b.prsMerged,
            render: numericClickable('prsMerged'),
        },
        {
            title: <ColumnTitle label="PRs acted on" hint="Distinct PRs the user had at least one in-window action on (review, review-thread comment, or top-level PR comment). Click to see them." />,
            dataIndex: 'prsActedOn',
            key: 'prsActedOn',
            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.prsActedOn - b.prsActedOn,
            render: numericClickable('prsActedOn'),
        },
        {
            title: <ColumnTitle label="Lines changed" hint="Sum of additions + deletions across the PRs merged (left column). Same drill-down as PRs merged." />,
            dataIndex: 'locChanged',
            key: 'locChanged',
            align: 'right',
            sorter: (a, b) => a.locChanged - b.locChanged,
            render: numericClickable('prsMerged'),
        },
        {
            title: <ColumnTitle label="Issues opened" hint="Issues authored by this user (not PRs) where created_at falls inside the window. Click to see them." />,
            dataIndex: 'issuesOpened',
            key: 'issuesOpened',
            align: 'right',
            sorter: (a, b) => a.issuesOpened - b.issuesOpened,
            render: numericClickable('issuesOpened'),
        },
        {
            title: <ColumnTitle label="Issues closed" hint="Issues closed by this user inside the window. Credit goes to whoever performed the close event. Click to see them." />,
            dataIndex: 'issuesClosed',
            key: 'issuesClosed',
            align: 'right',
            sorter: (a, b) => a.issuesClosed - b.issuesClosed,
            render: numericClickable('issuesClosed'),
        },
        {
            title: <ColumnTitle label="Issues acted on" hint="Distinct issues with an in-window timeline event by this user — comment, assignment (as assignee), close, or reopen. Click to see them." />,
            dataIndex: 'issuesActedOn',
            key: 'issuesActedOn',
            align: 'right',
            sorter: (a, b) => a.issuesActedOn - b.issuesActedOn,
            render: numericClickable('issuesActedOn'),
        },
        {
            title: <ColumnTitle label="Docs created" hint="Google Drive files the user created in the configured shared folder tree during the window. Click to see them." />,
            dataIndex: 'docsCreated',
            key: 'docsCreated',
            align: 'right',
            sorter: (a, b) => a.docsCreated - b.docsCreated,
            render: numericClickable('docsCreated'),
        },
        {
            title: <ColumnTitle label="Demos" hint="Demos & presentations the user gave, sourced from the Design/Demo Meetings sheet. Click to see them." />,
            dataIndex: 'demos',
            key: 'demos',
            align: 'right',
            sorter: (a, b) => a.demos - b.demos,
            render: numericClickable('demos'),
        },
        ...(coderTemplates.length > 0
            ? [
                  {
                      title: (
                          <ColumnTitle
                              label="Coder"
                              hint="Coder self-hosted dev-environment usage. Active workspace time (live VS Code / SSH / terminal / app connections; idle workspaces don't count). Total plus a breakdown per template."
                          />
                      ),
                      children: [
                          {
                              title: 'Total',
                              key: 'coderActiveSeconds',
                              align: 'right' as const,
                              sorter: (a: TeamMemberInsight, b: TeamMemberInsight) =>
                                  (a.coderActiveSeconds ?? 0) - (b.coderActiveSeconds ?? 0),
                              render: (_: unknown, row: TeamMemberInsight) =>
                                  formatDuration(row.coderActiveSeconds),
                          },
                          ...coderTemplates.map((tpl) => ({
                              title: tpl,
                              key: `coder_${tpl}`,
                              align: 'right' as const,
                              sorter: (a: TeamMemberInsight, b: TeamMemberInsight) =>
                                  (a.coderTemplateSeconds?.[tpl] ?? 0) - (b.coderTemplateSeconds?.[tpl] ?? 0),
                              render: (_: unknown, row: TeamMemberInsight) =>
                                  formatDuration(row.coderTemplateSeconds?.[tpl]),
                          })),
                      ],
                  },
              ]
            : []),
        ...EVENT_TYPES.map((type) => ({
            title: (
                <ColumnTitle
                    label={EVENT_TYPE_LABELS[type]}
                    hint={`Curated "${EVENT_TYPE_LABELS[type]}" events entered on the Member Events page. Click to see them.`}
                />
            ),
            key: `event_${type}`,
            align: 'right' as const,
            sorter: (a: TeamMemberInsight, b: TeamMemberInsight) =>
                countEventsOfType(a, type) - countEventsOfType(b, type),
            render: (_: unknown, row: TeamMemberInsight) =>
                numericClickable('events', type)(countEventsOfType(row, type), row),
        })),
    ];
};

const TeamInsightsTable: React.FC<Props> = ({ rows, coderTemplates, loading, error, onCellClick }) => {
    if (error) {
        return (
            <Alert
                type="error"
                showIcon
                message="Couldn't load team insights"
                description={error}
            />
        );
    }

    return (
        <Table
            rowKey="login"
            columns={buildColumns(coderTemplates, onCellClick)}
            dataSource={rows}
            loading={loading}
            pagination={false}
            locale={{
                emptyText: (
                    <Typography.Text type="secondary">
                        No org members or activity in this window.
                    </Typography.Text>
                ),
            }}
        />
    );
};

export default TeamInsightsTable;
